import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { computed } from 'mobx';
import { DateTime } from 'src_common/utils/time/time';
import { BlockingNotificationType, NotificationType } from 'src_server/trpc/types/accountModel/accountResponse';
import { Common } from 'src/domains/common/Common';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import {
    buildUpdatedNotificationsOnConfirm,
    buildUpdatedNotificationsOnShown,
    findActiveNotification,
} from 'src/domains/players/state/notificationTimestamps';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getMarkersOfHarmPopupByName';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { extractMarkersOfHarmTriggerId } from 'src/domains/players/state/blockingNotificationUtils';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

export class BlockingNotificationsState {
    private readonly basicDataComp: BasicDataModel;
    private readonly markersOfHarmPopupMap: AutoMap<number, Resource<Response200Type | null>>;

    public constructor(private readonly common: Common) {
        this.basicDataComp = BasicDataModel.get(this.common);
        this.markersOfHarmPopupMap = new AutoMap(
            (triggerId: number) =>
                new Resource(async (): Promise<Response200Type | null> => {
                    try {
                        const response = await this.common.trpcClient.client.cms.getMarkersOfHarmPopupByTriggerId.query(
                            {
                                triggerId,
                            }
                        );

                        if (response.status === 200) {
                            return response.body;
                        }

                        return null;
                    } catch (error) {
                        console.error('Error fetching markers of harm popup:', error);
                        return null;
                    }
                })
        );
    }

    public static get = AutoWeakMap.create((common: Common) => new BlockingNotificationsState(common));

    @computed public get getBlockingNotificationType(): NotificationType | null {
        const customerData = this.basicDataComp.basicDataReady;
        if (customerData === null) {
            return null;
        }

        const allNotifications = customerData.prompts?.notificationList ?? [];
        const activeNotifications = [...allNotifications].filter((notification) => notification.active);

        for (const notification of activeNotifications) {
            switch (notification.name) {
                case 'changeOfLicense':
                    return BlockingNotificationType.CHANGE_OF_LICENSE;
                case 'privacyUpdate':
                    return BlockingNotificationType.PRIVACY_UPDATE;
                case 'termsUpdate':
                    return BlockingNotificationType.TERMS_UPDATE;
                case 'contactPreferencesUpdate':
                    return BlockingNotificationType.CONTACT_PREFERENCES;
                case 'ringfencedFundsUpdate':
                    return BlockingNotificationType.RINGFENCED_FUNDS_UPDATE;
                case 'rtpUpdate':
                    return BlockingNotificationType.RTP_UPDATE;
                case 'depositLimitCalendarUpdate':
                    return BlockingNotificationType.DEPOSIT_LIMIT_CALENDAR_UPDATE;
                case 'depositLimitCalendarUpdatePreview':
                    return BlockingNotificationType.DEPOSIT_LIMIT_CALENDAR_UPDATE_PREVIEW;
                case 'gamstop-previously-positive':
                    if (ConfigComponents.get(this.common).config.gamstopPreviouslyPositivePopup) {
                        return BlockingNotificationType.GAMSTOP_PREVIOUSLY_POSITIVE;
                    }
                    break;
                case 'kycDepositRestriction':
                    if (
                        (customerData.depositRestriction?.source === 'kycThreshold' ||
                            customerData.depositRestriction?.source === 'backoffice') &&
                        this.checkIsDepositRestrictionActive()
                    ) {
                        return BlockingNotificationType.KYC_DEPOSIT_RESTRICTION;
                    }
                    break;
                default: {
                    const triggerId = extractMarkersOfHarmTriggerId(notification.name);

                    if (triggerId === null) {
                        break;
                    }

                    const popupResource = this.markersOfHarmPopupMap.get(triggerId).get();
                    if (popupResource.type === 'loading') {
                        return notification.name;
                    }

                    if (popupResource.type === 'ready' && popupResource.value !== null) {
                        return notification.name;
                    }

                    break;
                }
            }
        }

        return null;
    }

    @computed public get getActiveCmsMarkersOfHarmPopup(): Response200Type | null {
        const notificationType = this.getBlockingNotificationType;

        if (notificationType === null) {
            return null;
        }

        const triggerId = extractMarkersOfHarmTriggerId(notificationType);
        if (triggerId === null) {
            return null;
        }

        const popup = this.markersOfHarmPopupMap.get(triggerId).get();
        return popup.type === 'ready' ? popup.value : null;
    }

    private checkIsDepositRestrictionActive = (): boolean => {
        const depositRestriction = this.basicDataComp.basicDataReady?.depositRestriction ?? null;

        if (depositRestriction === null) {
            return false;
        }

        if (depositRestriction.permanent === true) {
            return true;
        }

        if (DateTime.current().isBefore(depositRestriction.expiryDate ?? '')) {
            return true;
        }

        return false;
    };

    public handleConfirmNewLicenceNotification = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.CHANGE_OF_LICENSE);
    };

    public handleConfirmContactPreferences = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.CONTACT_PREFERENCES);
    };
    public handleConfirmRingfencedfunds = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.RINGFENCED_FUNDS_UPDATE);
    };
    public handleConfirmRTPNotification = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.RTP_UPDATE);
    };

    public handleConfirmNewPrivacyPolicy = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.PRIVACY_UPDATE);
    };

    public handleConfirmNewTerms = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.TERMS_UPDATE);
    };

    public handleConfirmGamstopPreviouslyPositive = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.GAMSTOP_PREVIOUSLY_POSITIVE);
    };

    public handleConfirmKycDepositRestrictionNotification = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.KYC_DEPOSIT_RESTRICTION);
    };

    public handleConfirmDepositLimitCalendarUpdate = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.DEPOSIT_LIMIT_CALENDAR_UPDATE);
    };

    public handleConfirmDepositLimitCalendarUpdatePreview = async (): Promise<void> => {
        await this.onConfirm(BlockingNotificationType.DEPOSIT_LIMIT_CALENDAR_UPDATE_PREVIEW);
    };

    public handleConfirmCmsNotification = async (
        notificationName: string,
        options?: { skipFinalRefresh?: boolean }
    ): Promise<void> => {
        await this.onConfirm(notificationName, options);
    };

    public markNotificationShown = async (notificationName: string): Promise<void> => {
        const hasActiveNotification =
            this.basicDataComp.basicDataReady?.prompts?.notificationList.some(
                (item) => item.name === notificationName && item.active
            ) ?? false;

        if (hasActiveNotification === false) {
            return;
        }

        await this.basicDataComp.basicData.refreshAndWait();

        const allNotifications = this.basicDataComp.basicDataReady?.prompts?.notificationList ?? [];
        const notification = findActiveNotification(allNotifications, notificationName);

        if (notification?.active !== true) {
            return;
        }

        const shownAt = new Date().toISOString();

        await this.common.trpcClient.client.accounts.approveNotification.mutate({
            prompts: {
                notificationList: buildUpdatedNotificationsOnShown(allNotifications, notificationName, shownAt),
            },
        });

        this.basicDataComp.basicData.refresh();
    };

    private onConfirm = async (notificationName: string, options?: { skipFinalRefresh?: boolean }): Promise<void> => {
        await this.basicDataComp.basicData.refreshAndWait();

        const allNotifications = this.basicDataComp.basicDataReady?.prompts?.notificationList ?? [];

        if (findActiveNotification(allNotifications, notificationName) === undefined) {
            return;
        }

        const closedAt = new Date().toISOString();

        await this.common.trpcClient.client.accounts.approveNotification.mutate({
            prompts: {
                notificationList: buildUpdatedNotificationsOnConfirm(allNotifications, notificationName, closedAt),
            },
        });

        if (options?.skipFinalRefresh === true) {
            return;
        }

        await this.basicDataComp.basicData.refreshAndWait();
    };
}
